import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Table, Button, Badge,
  Form, InputGroup, Modal
} from 'react-bootstrap';
import {
  FaPlus, FaSearch, FaFilter, FaEye,
  FaTrash, FaUserPlus, FaCalendar, FaClock
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../../components/common/Header';
import InquiryService from '../../../services/inquiryService';
import AddInquiryModal from './AddInquiryModal';

const InquiryManagement = () => {
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [todayFollowUps, setTodayFollowUps] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showTodayOnly, setShowTodayOnly] = useState(false);

  useEffect(() => {
    loadInquiries();
  }, []);

  useEffect(() => {
    filterInquiries();
  }, [inquiries, searchTerm, statusFilter, showTodayOnly]);

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const response = await InquiryService.getAllInquiries();
      setInquiries(response.data.inquiries || []);
      setTodayFollowUps(response.data.today_follow_ups || 0);
    } catch (error) {
      toast.error('Failed to load inquiries');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterInquiries = () => {
    let filtered = [...inquiries];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(i =>
        i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.phone.includes(searchTerm) ||
        (i.email && i.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(i => i.status === statusFilter);
    }

    // Today's follow-ups filter
    if (showTodayOnly) {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(i => i.follow_up_date === today);
    }

    setFilteredInquiries(filtered);
  };

  const handleAddClick = () => {
    setSelectedInquiry(null);
    setShowAddModal(true);
  };

  const handleEditClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowAddModal(true);
  };

  const handleDeleteClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await InquiryService.deleteInquiry(selectedInquiry.id);
      toast.success('Inquiry deleted successfully');
      loadInquiries();
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const handleConvertToStudent = async (id) => {
    try {
      await InquiryService.convertToStudent(id);
      toast.success('Inquiry converted to student');
      loadInquiries();
    } catch (error) {
      toast.error('Failed to convert inquiry');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'contacted':
        return <Badge bg="info">Contacted</Badge>;
      case 'converted':
        return <Badge bg="success">Converted</Badge>;
      case 'lost':
        return <Badge bg="danger">Lost</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getFollowUpColor = (date) => {
    if (!date) return 'text-muted';
    
    const today = new Date().toISOString().split('T')[0];
    if (date === today) return 'text-warning fw-bold';
    if (date < today) return 'text-danger';
    return 'text-success';
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2>
              <FaCalendar className="me-2 text-primary" />
              Inquiry Management
            </h2>
          </Col>
          <Col className="text-end">
            <Button variant="success" onClick={handleAddClick}>
              <FaPlus className="me-2" /> Add New Inquiry
            </Button>
          </Col>
        </Row>

        {/* Stats Card */}
        <Row className="mb-4">
          <Col md={4}>
            <Card className="shadow-sm border-0 bg-info text-white">
              <Card.Body>
                <h6>Total Inquiries</h6>
                <h3>{inquiries.length}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="shadow-sm border-0 bg-warning text-white">
              <Card.Body>
                <h6>Pending</h6>
                <h3>{inquiries.filter(i => i.status === 'pending').length}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="shadow-sm border-0 bg-success text-white">
              <Card.Body>
                <h6>Follow-ups Today</h6>
                <h3>{todayFollowUps}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row>
              <Col md={5}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by name, phone or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaFilter />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="today-switch"
                  label="Show only today's follow-ups"
                  checked={showTodayOnly}
                  onChange={(e) => setShowTodayOnly(e.target.checked)}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Inquiries Table */}
        <Card className="shadow-sm">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table striped hover responsive>
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Name/Contact</th>
                    <th>Interested Course</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Follow-up Date</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInquiries.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        No inquiries found
                      </td>
                    </tr>
                  ) : (
                    filteredInquiries.map((inquiry, index) => (
                      <tr key={inquiry.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{inquiry.name}</strong>
                          <br />
                          <small className="text-muted">
                            {inquiry.phone}
                            {inquiry.email && ` | ${inquiry.email}`}
                          </small>
                        </td>
                        <td>
                          {inquiry.course_name || 'Not specified'}
                        </td>
                        <td>
                          {inquiry.message ? (
                            <span title={inquiry.message}>
                              {inquiry.message.substring(0, 30)}...
                            </span>
                          ) : '-'}
                        </td>
                        <td>{getStatusBadge(inquiry.status)}</td>
                        <td>
                          <span className={getFollowUpColor(inquiry.follow_up_date)}>
                            <FaClock className="me-1" />
                            {inquiry.follow_up_date || 'Not set'}
                          </span>
                        </td>
                        <td>{inquiry.created_by_name || 'Admin'}</td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEditClick(inquiry)}
                            title="View/Edit"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            className="me-1"
                            onClick={() => handleConvertToStudent(inquiry.id)}
                            title="Convert to Student"
                            disabled={inquiry.status === 'converted'}
                          >
                            <FaUserPlus />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteClick(inquiry)}
                            title="Delete"
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Modal */}
        <AddInquiryModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onSuccess={loadInquiries}
          inquiryToEdit={selectedInquiry}
        />

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">
              <FaTrash className="me-2" />
              Confirm Delete
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete this inquiry?</p>
            {selectedInquiry && (
              <p className="fw-bold text-danger">{selectedInquiry.name}</p>
            )}
            <p className="text-muted small">This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete Inquiry
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default InquiryManagement;